import * as React from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Copyright from "../components/CopyRight";
import * as Yup from "yup";
import { Formik } from "formik";
import { useNavigate } from "react-router-dom";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import useAuth from "../hooks/useAuth";
import { useEffect } from "react";
import LoadingSpinner from "../components/LoadingSpinner";
import { ParseQueryString } from "../utils/apiService";
import googleImage from "../assets/images/google.png";
import { Avatar } from "@mui/material";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any; // Needed for Hubspot

export default function SignIn() {
  const navigate = useNavigate();
  const { signIn, signInGoogle, isAuthenticated, isAuthenticating } = useAuth();

  useEffect(() => {
    // Log Pageview with Hubspot
    const _hsq = window._hsq = window._hsq || [];
    _hsq.push(['setPath', '/signin']);
    _hsq.push(['trackPageView']);
  }, []);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Enter a valid email")
      .required("Email is required"),
    password: Yup.string()
      .min(8, "Password should be of minimum 8 characters length")
      .required("Password is required"),
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated]);

  const getLogoImage = () => {
    const queryParams = ParseQueryString(location.search)
    const sf = queryParams.get("sf")

    let storeName = "tsg"

    if (sf != undefined && sf != "" && sf != "tsg" ) {
      storeName = sf
    } else {
      const savedStorefront = localStorage.getItem("storefront")
      if (savedStorefront) {
        storeName = savedStorefront
      }
    }

    let baseURL = location.origin

    const colorMode = localStorage.getItem("colorMode")

    console.log("logo selection: colorMode=", colorMode, ", baseURL=", baseURL, ", storefront=", storeName)

    if (baseURL.indexOf("localhost") >= 0) {
      baseURL = "https://dev.portal.theshare.group"
    }

    let iconColorMode = "light"
    if (colorMode != undefined && colorMode == "dark") {
      iconColorMode = "dark"
    }

    const logoURL = baseURL + "/storefront/" + storeName + "_logo_" + iconColorMode + ".png"
    return logoURL
  }

  if (isAuthenticating || isAuthenticated) {
    return <LoadingSpinner title="Signing In..." textColor="#0352fc"/>;
  }

  const googleSignIn = async () => {
    await signInGoogle()
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <img src={getLogoImage()} height={48} />
        <Typography component="h1" variant="h5" sx={{marginTop:2}}>
          Sign in
        </Typography>
        <Formik
          initialValues={{
            email: "",
            password: "",
            submit: null,
          }}
          validationSchema={validationSchema}
          onSubmit={async (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            values: any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            { setErrors, setStatus, setSubmitting }: any
          ): Promise<void> => {
            try {
              setSubmitting(true);
              // user sign in
              await signIn({email: values.email.toLowerCase(), password: values.password});
              navigate("/", { replace: true });
              setSubmitting(false);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
              setStatus({ success: false });
              setErrors({ submit: err.message });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }): JSX.Element => (
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 1 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    value={values?.email}
                    onChange={handleChange}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    autoComplete="email"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    value={values?.password}
                    onChange={handleChange}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                    type="password"
                    id="password"
                    autoComplete="new-password"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox value="remember" color="primary" />}
                    label="Remember me"
                  />
                </Grid>
                <Grid item xs={12}>
                  {/*Display Error with Icon*/}
                  {errors?.submit && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ErrorOutlineIcon sx={{ color: "red", mr: 1 }} />
                      <Typography variant="body2" color="error">
                        {errors?.submit} If you do not have an account, click the 'Sign Up' link below.
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{ mt: 3, mb: 2 }}
                  >
                    Sign In
                  </Button>
                </Grid>
              </Grid>
              <Typography
                sx={{
                  width: "100%",
                  my: 2,
                  textAlign: "center",
                }}
              >
                {/* OR */}
              </Typography>

              <Grid container>
                <Grid item xs>
                  <Link href="/forgotpassword" variant="body2">
                    Forgot password?
                  </Link>
                </Grid>
                <Grid item>
                  <Link href="/signup" variant="body2">
                    {"Don't have an account? Sign Up"}
                  </Link>
                </Grid>
              </Grid>
            </Box>
          )}
        </Formik>

        <Button
          type="button"
          fullWidth
          variant="contained"
          onClick={googleSignIn}
          sx={{
            backgroundColor: "white",
            color: "black",
            my: 2,
            ":hover": {
              backgroundColor: "whitesmoke",
            },
          }}
          startIcon={
            <Avatar src={googleImage} sx={{ width: 20, height: 20 }} />
          }
        >
          Sign In with Google
        </Button>
      </Box>
      <Copyright sx={{ mt: 8, mb: 4 }} />
    </Container>
  );
}

